import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Bell, AlertTriangle, CheckCheck, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { useAgendaSocket } from "@/hooks/useAgendaSocket";

function tiempoRel(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const diff = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diff < 1) return "recién";
  if (diff < 60) return `hace ${diff} min`;
  const h = Math.floor(diff / 60);
  if (h < 24) return `hace ${h} h`;
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

export default function NotificationsBell() {
  const [alertas, setAlertas] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);

  const load = useCallback(() => {
    api
      .get("/alertas", { params: { limit: 30 } })
      .then(({ data }) => {
        setAlertas(data.alertas || []);
        setNoLeidas(data.no_leidas || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useAgendaSocket((msg) => {
    if (msg.tipo === "alerta" && msg.alerta) {
      setAlertas((prev) => [msg.alerta, ...prev.filter((a) => a.id !== msg.alerta.id)]);
      setNoLeidas((n) => n + 1);
      const fn = msg.alerta.nivel === "rojo" ? toast.error : toast.warning;
      fn(msg.alerta.titulo, { description: msg.alerta.mensaje });
    }
  });

  const marcarTodas = async () => {
    try {
      await api.post("/alertas/marcar-todas");
      setAlertas((prev) => prev.map((a) => ({ ...a, leida: true })));
      setNoLeidas(0);
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell">
          <Bell className="h-5 w-5 text-slate-600" />
          {noLeidas > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notifications-badge"
            >
              {noLeidas > 9 ? "9+" : noLeidas}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <p className="text-sm font-semibold text-slate-900">Alertas de capacidad</p>
          {noLeidas > 0 && (
            <button
              onClick={marcarTodas}
              className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="marcar-todas-leidas"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Marcar leídas
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {alertas.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10">Sin alertas. Todo bajo control.</p>
          ) : (
            alertas.map((al) => (
              <div
                key={al.id}
                className={`flex gap-2.5 px-4 py-3 border-b border-slate-50 ${al.leida ? "opacity-60" : ""}`}
                data-testid={`alerta-item-${al.id}`}
              >
                <div
                  className={`mt-0.5 h-7 w-7 shrink-0 rounded-full flex items-center justify-center ${
                    al.nivel === "rojo" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"
                  }`}
                >
                  {al.nivel === "rojo" ? <AlertTriangle className="h-4 w-4" /> : <TriangleAlert className="h-4 w-4" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 leading-tight">{al.titulo}</p>
                  <p className="text-[12px] text-slate-600 mt-0.5">{al.mensaje}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{tiempoRel(al.created_at)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
